import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { DELIVERY_STATUSES } from '../../../../../utils/catalogs';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { DateRangePicker } from '../../../../../components/ui/DateRangePicker';

/**
 * Search, range and status. The admin's seller, driver and upselling filters
 * mean nothing to a seller: the backend already scopes the listing to it. Nor
 * does the admin's "Filtro avanzado": that addon is the admin's alone and a
 * seller never inherits it, so there is nothing of it here.
 *
 * @param {object} props
 * @param {object} props.filters - The slice's `filters`
 * @param {(fieldOrPatch: string|object, value?: *) => void} props.onFilterChange
 */
export const DeliveryFilters = ({ filters, onFilterChange }) => {
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFilterChange('search', searchInput);
      }
    }, 1500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, onFilterChange]);

  const statusOptions = useMemo(() => [
    { value: 'all', label: 'Todos' },
    ...DELIVERY_STATUSES.map((s) => ({ value: s.system_name, label: s.user_name }))
  ], []);

  const hasActiveFilters = searchInput.trim() !== '' || filters.delivery_status !== 'all';

  return (
    <div data-testid="delivery-filters" className="flex flex-col md:flex-row flex-wrap items-center gap-4">
      <div className="flex-1 min-w-[300px] flex items-center gap-2 bg-white dark:bg-[#1a1a1a] border border-border rounded-xl px-4 py-2">
        <Search size={16} className="text-text-muted flex-shrink-0" />
        <input
          type="text"
          data-testid="delivery-search"
          placeholder="Buscar por orden, tracking, destino..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="bg-transparent text-sm w-full outline-none dark:text-white placeholder:text-text-muted"
        />
      </div>

      <DateRangePicker
        startDate={filters.start_date}
        endDate={filters.end_date}
        onChange={onFilterChange}
      />

      <FilterableSelect
        testId="delivery-status-filter"
        label="Estado:"
        value={filters.delivery_status}
        onChange={(val) => onFilterChange('delivery_status', val)}
        options={statusOptions}
      />

      {hasActiveFilters && (
        <button
          onClick={() => {
            setSearchInput('');
            onFilterChange({ search: '', delivery_status: 'all' });
          }}
          className="text-sm font-medium text-text-muted hover:text-ninja-dark dark:hover:text-white transition-colors px-2 ml-auto"
        >
          Limpiar filtros
        </button>
      )}
    </div>
  );
};
